import React, { Fragment } from 'react';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import useClipboard from 'react-use-clipboard';
import moment from 'moment';

import Button from '../Button';
import usePrepaidPrintPackagesPurchase from './hooks/usePrepaidPrintPackagesPurchase';
import IdentitySelector from './components/identitySelector';
import ScrollableDiv from '../ScrollableDiv';
import AlbumPrintPackageOption from './components/AlbumPrintPackageOption';
import { getAlbumPrintPackageCartItem } from '../../../services/checkout/redux/helpers';
import { getCartTotalItems } from '../../../apps/web-gallery/utilities/web-gallery-cart-utils';
import {
  cartItemUpdate,
  toggleAlbumPrintPackageCartItem,
} from '../../../services/checkout/redux/checkoutActions';
import Alert from '../Alert';
import { DISCOUNT_MODE } from '../../../util/promo-codes';
import { formatPriceForDisplay } from '../../../util/format-money';
import CheckmarkIcon from '../../icons/CheckmarkIcon';

const propTypes = {
  albumFolderId: PropTypes.string.isRequired,
  cart: PropTypes.array.isRequired,
  activeIdentity: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  onAlbumPrintPackageToggle: PropTypes.func,
  onCartItemUpdate: PropTypes.func,
  onCheckout: PropTypes.func.isRequired,
  onAddFace: PropTypes.bool,
};

const PrepaidPrintPackagesPurchase = ({
  albumFolderId,
  cart,
  activeIdentity,
  onAlbumPrintPackageToggle,
  onCartItemUpdate,
  onCheckout,
  onAddFace,
}) => {
  const {
    identities,
    loadingIdentities,
    printPackages,
    activePromoCode,
    hasNoFaceRec,
  } = usePrepaidPrintPackagesPurchase({ albumFolderId });
  const [promoCodeCopied, setPromoCodeCopied] = useClipboard(
    activePromoCode?.code,
  );

  const cartTotalItems = getCartTotalItems(cart);
  const canCheckout = !loadingIdentities && cartTotalItems > 0;

  const scrollableContainerClasses = classNames({
    'prepaid-print-packages-scroller': true,
    'with-promo-code-alert': !!activePromoCode,
  });

  return (
    <Fragment>
      {activePromoCode && (
        <Alert type="info" className="prepaid-print-packages-alert">
          {activePromoCode.expires_at && (
            <p className="text-sm text-primary text-uppercase mb-0 text-extra-bold">
              LIMITED TIME OFFER!
            </p>
          )}
          Use promo code &quot;
          <span className="text-primary text-extra-bold">
            {activePromoCode.code}
          </span>
          &quot;{' '}
          {activePromoCode.expires_at ? (
            <>
              and purchase your prints prior to{' '}
              <span className="text-primary text-extra-bold">
                {moment.utc(activePromoCode.expires_at).format('MMMM D, yyyy')}
              </span>{' '}
            </>
          ) : (
            ''
          )}
          to save{' '}
          <span className="text-primary text-extra-bold">
            {activePromoCode.discount.mode === DISCOUNT_MODE.PERCENTAGE
              ? `${activePromoCode.discount.value}%`
              : `$${formatPriceForDisplay(
                  activePromoCode.discount.value,
                  true,
                )}`}
          </span>
          !
          <Button
            pill
            btnOutline
            btnSize="sm"
            className="prepaid-print-packages-promo-code-copy"
            onClick={() => setPromoCodeCopied(true)}
          >
            {promoCodeCopied ? (
              <Fragment>
                <div>
                  <CheckmarkIcon size={12} color="white" />
                </div>{' '}
                Copied to clipboard
              </Fragment>
            ) : (
              'Copy promo code'
            )}
          </Button>
        </Alert>
      )}
      <div className="h-100 w-100 d-flex flex-column">
        {!hasNoFaceRec && (
          <Fragment>
            <h4 className="w-100 text-left text-md-center">
              Select packages for:
            </h4>
            <IdentitySelector identities={identities} onAddFace={onAddFace} />
          </Fragment>
        )}
        {(activeIdentity || hasNoFaceRec) && (
          <ScrollableDiv className={scrollableContainerClasses}>
            {printPackages.map(albumFolderPrintPackage => (
              <AlbumPrintPackageOption
                key={albumFolderPrintPackage.id}
                albumPrintPackage={albumFolderPrintPackage}
                cartItem={getAlbumPrintPackageCartItem(cart, {
                  albumPrintPackageId: albumFolderPrintPackage.id,
                  identityId: activeIdentity?.id,
                })}
                onAlbumPrintPackageToggle={() =>
                  onAlbumPrintPackageToggle({
                    albumPrintPackage: albumFolderPrintPackage,
                    identity: activeIdentity,
                    prePaid: true,
                  })
                }
                onUpdate={onCartItemUpdate}
              />
            ))}
          </ScrollableDiv>
        )}
        {cart.length > 0 && (
          <div className="prepaid-print-packages-checkout-btn">
            <Button
              disabled={!canCheckout}
              btnBlock
              btnStyle="success"
              onClick={() => {
                if (!canCheckout) {
                  return;
                }

                onCheckout();
              }}
            >
              {!canCheckout ? (
                <span>Loading...</span>
              ) : (
                <Fragment>
                  Checkout{' '}
                  {cartTotalItems > 0 && (
                    <span>({pluralize('item', cartTotalItems, true)})</span>
                  )}
                </Fragment>
              )}
            </Button>
          </div>
        )}
      </div>
    </Fragment>
  );
};

PrepaidPrintPackagesPurchase.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: state.checkout.cart,
  activeIdentity: state.checkout.activeIdentity,
});

const mapDispatchToProps = {
  onAlbumPrintPackageToggle: toggleAlbumPrintPackageCartItem,
  onCartItemUpdate: cartItemUpdate,
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  PrepaidPrintPackagesPurchase,
);
